import { useEffect, useState, useRef } from 'react';
import { chatService } from '@services/chatService';
import { REACT_APP_ALLOWED_DOMAINS } from '../config';

const ALLOWED_DOMAINS: string[] = REACT_APP_ALLOWED_DOMAINS
  ? REACT_APP_ALLOWED_DOMAINS.split(',')
  : [
    'localhost:3001',
    'localhost:3000',
  ];

interface UseChatTokenReturn {
  token: string | null;
}

export const useChatToken = (
  onTokenReceived: (token: string, conversationId: number) => void,
  /** Called immediately when ACCESS_TOKEN differs from previous — before getConversations */
  onTokenChanging?: (nextToken: string, prevToken: string) => void,
): UseChatTokenReturn => {
  const [token, setToken] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const onTokenReceivedRef = useRef(onTokenReceived);
  const onTokenChangingRef = useRef(onTokenChanging);
  onTokenReceivedRef.current = onTokenReceived;
  onTokenChangingRef.current = onTokenChanging;

  useEffect(() => {
    if (initializedRef.current) return;

    const storedToken = globalThis.sessionStorage.getItem('access_token');
    if (storedToken) {
      setToken(storedToken);
      initializedRef.current = true;

      const storedConversationId = globalThis.sessionStorage.getItem('current_conversation_id');

      const initializeChat = async () => {
        try {
          let conversationId = storedConversationId ? parseInt(storedConversationId, 10) : null;

          if (!conversationId) {
            const response = await chatService.getConversations(storedToken);
            if (response.data) {
              conversationId = response.data;
              globalThis.sessionStorage.setItem('current_conversation_id', conversationId!.toString());
            }
          }

          if (conversationId) {
            onTokenReceivedRef.current(storedToken, conversationId);
          }
        } catch (error) {
          console.error('Failed to initialize chat from storage:', error);
        }
      };

      initializeChat();
    }
  }, []);

  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      const origin = new URL(event.origin);
      const originDomain = origin.port
        ? `${origin.hostname}:${origin.port}`
        : origin.hostname;

      if (
        !ALLOWED_DOMAINS.some(
          (domain) =>
            originDomain === domain ||
            origin.hostname.endsWith(domain) ||
            (domain.startsWith('localhost') && origin.hostname === 'localhost')
        )
      ) {
        console.warn('Blocked message from origin:', event.origin);
        return;
      }

      if (event.data?.type === 'ACCESS_TOKEN') {
        const receivedToken = event.data.token as string;
        const prevToken = globalThis.sessionStorage.getItem('access_token');

        if (prevToken && prevToken !== receivedToken) {
          onTokenChangingRef.current?.(receivedToken, prevToken);
        }

        globalThis.sessionStorage.setItem('access_token', receivedToken);
        setToken(receivedToken);
        initializedRef.current = true;

        try {
          const response = await chatService.getConversations(receivedToken);
          const conversationId = response.data;

          if (conversationId) {
            globalThis.sessionStorage.setItem('current_conversation_id', conversationId.toString());
            onTokenReceivedRef.current(receivedToken, conversationId);
          }
        } catch (error) {
          console.error('Lỗi khi tạo conversation:', error);
        }
      }

    };

    globalThis.addEventListener('message', handler);

    return () => {
      globalThis.removeEventListener('message', handler);
    };
  }, []);

  return { token };
};
